import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, finalize, tap } from "rxjs/operators";
import { share } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";
import { LayoutService } from "../services/layout.service";
import { StorageService } from "../services/storage.service";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class Interceptor implements HttpInterceptor {
  constructor(
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private router: Router,
    private loaderService: LayoutService,
    private storageService: StorageService
  ) {}

  isLoginPage(): boolean {
    return this.router.url === "/auth/login";
  }
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const user = this.storageService.getFromLocalOrSessionStorage("user");
    let token: string | null = null;
    if (user) {
      token = user.token;
    }

    if (token) {
      request = request.clone({
        headers: request.headers.set("Authorization", `Bearer ${token}`),
      });
    }

    request = request.clone({
      headers: request.headers.set("Accept", "application/json"),
    });

    // Show loader
    const showLoader = !request.params.has('noLoader');
    if (showLoader) {
      this.loaderService.show();
    }

    return this.makeRequest(request, next);
  }

  makeRequest(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const req: Observable<HttpEvent<any>> = next.handle(request).pipe(
      share(),
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (request.method !== "GET") {
            if (event.body && event.body.success) {
              this.toastrService.success(
                event.body.message,
                '',
                {
                  positionClass: "toast-bottom-right",
                  tapToDismiss: true,
                }
              );
            } else if (event.body && !event.body.success) {
              this.toastrService.error(event.body.message, this.translateService.instant("warning"), {
                positionClass: "toast-bottom-right",
                tapToDismiss: true,
              });
            }
          }
        }
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.sessionExpired(err);
        } else if (err.status === 422) {
          if (err && err.error) {
            const errorMessages = Array.isArray(err.error.errors)
              ? "<ul>" +
                err.error.errors.map((e: string) => `<li>${e}</li>`).join("") +
                "</ul>"
              : err.error.message;
            this.toastrService.error(errorMessages, this.translateService.instant("warning"), {
              positionClass: "toast-bottom-right",
              tapToDismiss: true,
            });
          }
        } else {
          // Generic error
          if (
            err.status == null ||
            err.status == undefined ||
            err.status == 0 ||
            !navigator.onLine
          ) {
            this.toastrService.error(
              this.translateService.instant("errors.connection_absent"),
              this.translateService.instant("warning"),
              {
                positionClass: "toast-bottom-right",
                tapToDismiss: true,
              }
            );
          } else {
            const errorMessage = err.error?.message || this.translateService.instant(
              "errors.not_know"
            );
            console.error(errorMessage);
            this.toastrService.error(
              errorMessage,
              this.translateService.instant(
                "errors.generic_error"
              ),
              {
                positionClass: "toast-bottom-right",
                tapToDismiss: true,
              }
            );
          }
        }
        return throwError(err);
      }),
      finalize(() => {
        // Hide loader
        if (!request.params.has('noLoader')) {
          this.loaderService.hide();
        }
      })
    );

    return req;
  }

  sessionExpired(err: any) {
    this.storageService.clearStorage();

    if (err instanceof HttpErrorResponse) {
      try {
        if (!this.isLoginPage()) {
          this.toastrService.error(this.translateService.instant(
            "errors.session_expired"
          ), err.statusText, {
            positionClass: "toast-bottom-right",
            tapToDismiss: true,
          });
        } else {
          const errorTranslate = this.translateService.instant('errors.access_error');
          this.toastrService.error(
            err.error.message,
            errorTranslate,
            {
              positionClass: "toast-bottom-right",
              tapToDismiss: true,
            }
          );
        }
      } catch (e) {
        const genericError = this.translateService.instant(
          "errors.generic_error"
        );
        this.toastrService.error(genericError, "", {
          positionClass: "toast-bottom-right",
          tapToDismiss: true,
        });
      } finally {
        setTimeout(
          () => (window.location.href = window.location.origin + "/auth/login"),
          3000
        );
      }
    }
  }
}
